import type { CSSProperties } from 'react';
import { twMerge } from 'tailwind-merge';
import type { ApexOptions } from 'apexcharts';
import type { Dictionary } from '@witmetrics/utils';
import { deepMerge } from '@witmetrics/utils/objects';
import Chart, { type ChartProps } from '@/charts/Chart';
import Box, { type BoxProps } from '@/components/Box';
import type { BarChartParams } from './types';
import { classes, formatData, styles } from './utils';

export interface BarChartProps<T extends Dictionary> extends BarChartParams<T> {
  legend?: boolean;
  orientation?: 'horizontal' | 'vertical';
  stacked?: boolean;
  dataLabels?: boolean;
  hideZeroLabels?: boolean;
  animations?: boolean;
  colors?: string[];
  fill?: ApexOptions['fill'];
  columnWidth?: string;
  barRadius?: number;
  barGap?: number;
  dataLabelsPosition?: 'top' | 'center';
  dataLabelColors?: string[];
  dataLabelChips?: boolean;
  formatValue?: (value: number) => string;
  className?: string;
  style?: CSSProperties;
  sx?: BoxProps['sx'];
  width?: ChartProps['width'];
  height?: ChartProps['height'];
  onMount?: ChartProps['onMount'];
  onSelect?: (key: keyof T, data: T) => void;
}

export default function BarChart<T extends Dictionary>({
  className,
  style,
  sx = {},
  legend = true,
  orientation = 'vertical',
  stacked = false,
  dataLabels = true,
  hideZeroLabels = false,
  animations = true,
  colors,
  fill,
  columnWidth,
  barRadius,
  barGap,
  dataLabelsPosition,
  dataLabelColors,
  dataLabelChips,
  formatValue,
  width = '100%',
  height = '250px',
  data,
  xAxis,
  series,
  labels = {},
  onMount,
  onSelect,
}: BarChartProps<T>) {
  const chartData = formatData(
    { data, xAxis, series, labels },
    {
      orientation,
      stacked,
      dataLabels,
      hideZeroLabels,
      animations,
      legend,
      formatValue,
      colors,
      fill,
      columnWidth,
      barRadius,
      barGap,
      dataLabelsPosition,
      dataLabelColors,
      dataLabelChips,
    }
  );

  const handleSelect: ChartProps['onSelect'] = ({
    seriesIndex,
    dataPointIndex,
  }) => {
    onSelect?.(series[seriesIndex], data[dataPointIndex]);
  };

  return (
    <Box
      style={style}
      sx={deepMerge(styles.wrapper, sx || {})}
      className={twMerge(classes.wrapper, className)}>
      <Chart
        type="bar"
        width={width}
        height={height}
        onMount={onMount}
        onSelect={handleSelect}
        {...chartData}
      />
    </Box>
  );
}
